/**
 * Cohort Retention Heatmap
 * Beautiful heatmap showing retention rates by cohort and month
 */

import { CohortDataPoint } from '../../types/analytics';

interface CohortHeatmapProps {
  cohorts: CohortDataPoint[];
  maxMonths?: number;
}

export function CohortHeatmap({ cohorts, maxMonths = 12 }: CohortHeatmapProps) {
  if (cohorts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Nessun dato cohort disponibile
      </div>
    );
  }

  // Get color based on retention rate
  const getColor = (rate: number): string => {
    if (rate >= 80) return 'bg-green-600 text-white';
    if (rate >= 60) return 'bg-green-500 text-white';
    if (rate >= 40) return 'bg-yellow-500 text-white';
    if (rate >= 20) return 'bg-orange-500 text-white';
    if (rate >= 10) return 'bg-red-500 text-white';
    if (rate > 0) return 'bg-red-600 text-white';
    return 'bg-gray-100 text-gray-400';
  };

  // Format month label
  const formatMonth = (monthStr: string): string => {
    const [year, month] = monthStr.split('-');
    const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`;
  };

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <table className="border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-white border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-700">
                Cohort
              </th>
              <th className="border border-gray-200 px-4 py-2 text-center text-xs font-medium text-gray-700">
                Dimensione
              </th>
              {Array.from({ length: maxMonths + 1 }, (_, i) => (
                <th
                  key={i}
                  className="border border-gray-200 px-3 py-2 text-center text-xs font-medium text-gray-700 whitespace-nowrap"
                >
                  Mese {i}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohorts.map((cohort, cohortIndex) => (
              <tr key={cohort.acquisitionMonth} className="hover:bg-gray-50">
                <td className="sticky left-0 z-10 bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                  {formatMonth(cohort.acquisitionMonth)}
                </td>
                <td className="border border-gray-200 px-4 py-2 text-center text-sm text-gray-900">
                  {cohort.customersAcquired}
                </td>
                {Array.from({ length: maxMonths + 1 }, (_, monthOffset) => {
                  const rate = cohort.retentionRateByMonth[monthOffset];
                  const count = cohort.retentionByMonth[monthOffset];

                  // Don't show future months
                  if (rate === undefined) {
                    return (
                      <td key={monthOffset} className="border border-gray-200 bg-gray-50">
                        <div className="w-16 h-12"></div>
                      </td>
                    );
                  }

                  return (
                    <td key={monthOffset} className="border border-gray-200 p-0">
                      <div
                        className={`w-16 h-12 flex flex-col items-center justify-center ${getColor(rate)} transition-all hover:scale-105 cursor-pointer`}
                        title={`${count} clienti attivi (${rate.toFixed(1)}%)`}
                      >
                        <div className="text-xs font-bold">{rate.toFixed(0)}%</div>
                        <div className="text-xs opacity-75">({count})</div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center gap-4 text-xs">
        <span className="font-medium text-gray-700">Retention Rate:</span>
        <div className="flex items-center gap-2">
          <div className="bg-green-600 w-4 h-4 rounded"></div>
          <span>80%+</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-green-500 w-4 h-4 rounded"></div>
          <span>60-79%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-yellow-500 w-4 h-4 rounded"></div>
          <span>40-59%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-orange-500 w-4 h-4 rounded"></div>
          <span>20-39%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-red-500 w-4 h-4 rounded"></div>
          <span>10-19%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-red-600 w-4 h-4 rounded"></div>
          <span>0-9%</span>
        </div>
      </div>
    </div>
  );
}
